/**
 * Settings → Rate Card — ADMIN ONLY (App.tsx route is roles=['admin']).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  Quotes RESTORE — Stage 1 (2026-06-14). The editable INR + USD rate card.
 * ─────────────────────────────────────────────────────────────────────────
 *  The previous AVBP version BUILT a services + rate-card admin
 *  (`src/_legacy/routes/ServicesAdmin.tsx`); it was parked in `_legacy/`.
 *  This restores it into the CURRENT portal shell / nav / auth, using the
 *  current `ui.tsx` primitives + the pure `lib/rateCard.ts` seam. It loads
 *  the `services/{svcId}` catalogue and lets an admin VIEW + EDIT each
 *  service: name, rINR, rUSD, HSN, deliverables, and the default-milestone
 *  template. Tier / unit / firmCompat are identity fields, shown read-only.
 *
 *  L3B is FORMULA-priced — its base rate is ignored (the calculator drives
 *  the amount); the row shows the L3B formula summary + the locked-constant
 *  worked example instead of an editable base price.
 *
 *  Writes go through `updateService` → `services/{id}` (rules already allow
 *  admin write — NO rules change). Stage 2 (quote builder + PDF + send) and
 *  Stage 3 (project/money wiring) are LATER dispatches; this screen is the
 *  rate-card source of truth they will read.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { Box, Btn3, Card, EmptyNote, Tag } from '../components/ui';
import {
  listenServices,
  updateService,
  createService,
  editableFrom,
  calculateL3B,
  formatINR,
  formatUSD,
  DUE_RULE_OPTIONS,
  HSN_OPTIONS,
  type ServiceCatalogEntry,
  type ServiceEditableFields,
  type ServiceMilestone,
  type ServiceTier,
} from '../lib/rateCard';

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
  boxSizing: 'border-box',
};

function Lbl({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--ink-mute)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        margin: '10px 0 6px',
      }}
    >
      {children}
    </div>
  );
}

export default function SettingsRateCard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const actorName =
    user?.displayName || user?.email?.split('@')[0] || 'Admin';

  const [rows, setRows] = useState<ServiceCatalogEntry[] | null>(null);
  const [loadErr, setLoadErr] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [edit, setEdit] = useState<ServiceEditableFields | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsub = listenServices(
      (list) => {
        setRows(list);
        setLoadErr(false);
      },
      () => {
        setLoadErr(true);
        toast('Could not load the rate card (check connection).');
      },
    );
    return unsub;
  }, [toast]);

  function open(svc: ServiceCatalogEntry) {
    if (expandedId === svc.svcId) {
      setExpandedId(null);
      setEdit(null);
      return;
    }
    setExpandedId(svc.svcId);
    setEdit(editableFrom(svc));
  }

  function patch(p: Partial<ServiceEditableFields>) {
    setEdit((prev) => (prev ? { ...prev, ...p } : prev));
  }

  function patchMilestone(idx: number, p: Partial<ServiceMilestone>) {
    setEdit((prev) =>
      prev
        ? {
            ...prev,
            defaultMilestones: prev.defaultMilestones.map((m, i) =>
              i === idx ? { ...m, ...p } : m,
            ),
          }
        : prev,
    );
  }

  function addMilestone() {
    setEdit((prev) =>
      prev
        ? {
            ...prev,
            defaultMilestones: [
              ...prev.defaultMilestones,
              {
                label: 'Milestone',
                isAdvance: false,
                pct: 0,
                dueRule: 'on_milestone',
                deliverables: '',
              },
            ],
          }
        : prev,
    );
  }

  function removeMilestone(idx: number) {
    setEdit((prev) =>
      prev
        ? {
            ...prev,
            defaultMilestones: prev.defaultMilestones.filter(
              (_, i) => i !== idx,
            ),
          }
        : prev,
    );
  }

  async function save(svcId: string) {
    if (!edit || saving) return;
    setSaving(true);
    try {
      await updateService(svcId, edit, actorName);
      toast(`${edit.name} saved.`);
      setExpandedId(null);
      setEdit(null);
    } catch (e) {
      toast(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  /* ── "New service" (Phase 3d, 2026-07-03) — mint a catalogue entry through
   * the SAME services/{id} store the editor writes. It appears in this list,
   * the quote line picker and every canonical-name read the moment the doc
   * lands (the pickers read the live-merged catalogue). A new service is a
   * QUOTE-LINE service — it does not add a lead TIER (the tier set is the
   * frozen workflow config mirrored in the functions repo). ── */
  const [creating, setCreating] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newTier, setNewTier] = useState<ServiceTier>('CUSTOM');
  const [newFields, setNewFields] = useState<ServiceEditableFields>({
    name: '',
    desc: '',
    rINR: 0,
    rUSD: 0,
    defaultHsn: '998321',
    deliverables: '',
    defaultMilestones: [
      { label: 'Advance', isAdvance: true, pct: 50, dueRule: 'on_acceptance', deliverables: '' },
      { label: 'Delivery', isAdvance: false, pct: 50, dueRule: 'on_milestone', deliverables: '' },
    ],
  });
  const [createBusy, setCreateBusy] = useState(false);
  function patchNew(p: Partial<ServiceEditableFields>) {
    setNewFields((prev) => ({ ...prev, ...p }));
  }
  function patchNewMilestone(idx: number, p: Partial<ServiceMilestone>) {
    setNewFields((prev) => ({
      ...prev,
      defaultMilestones: prev.defaultMilestones.map((m, i) => (i === idx ? { ...m, ...p } : m)),
    }));
  }
  async function doCreate() {
    if (createBusy) return;
    setCreateBusy(true);
    try {
      const svcId = await createService(
        { svcId: newCode, tier: newTier, fields: newFields },
        actorName,
        rows ?? [],
      );
      toast(`Service ${svcId} created — it is live in the pickers now.`);
      setCreating(false);
      setNewCode('');
      setNewFields((prev) => ({ ...prev, name: '', desc: '', rINR: 0, rUSD: 0, deliverables: '' }));
    } catch (e) {
      toast(`Create failed: ${(e as Error).message}`);
    } finally {
      setCreateBusy(false);
    }
  }
  const canCreate =
    newCode.trim().length >= 2 && newFields.name.trim().length > 0 && !createBusy;

  return (
    <Box
      title="Rate card"
      note="Admin only — edit service names, INR / USD rates, HSN & deliverables"
    >
      <p
        style={{
          fontSize: 13,
          color: 'var(--ink-soft)',
          marginBottom: 14,
          lineHeight: 1.5,
        }}
      >
        The base price list for every service tier, in INR and USD. Edits save
        to the <code>services/</code> catalogue and become the source of truth
        for quotes. <b>L3B is formula-priced</b> — its amount is driven by the
        scale calculator (shown below), not a single base price.
      </p>

      {/* ── New service (Phase 3d) ── */}
      <div style={{ marginBottom: 14 }}>
        {!creating ? (
          <Btn3 intent="ink" size="sm" onClick={() => setCreating(true)}>
            + New service
          </Btn3>
        ) : (
          <Card flat>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}>
              New service
            </div>
            <p style={{ fontSize: 12, color: 'var(--ink-mute)', margin: '0 0 8px', lineHeight: 1.5 }}>
              Creates a quotable service in the <code>services/</code> catalogue —
              it appears in the quote line picker immediately. It does NOT add a
              project tier (tiers are the locked workflow set).
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: '0 1 160px' }}>
                <Lbl>Code</Lbl>
                <input
                  style={inputStyle}
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                  placeholder="e.g. GEO-SCAN"
                  aria-label="New service code"
                />
              </div>
              <div style={{ flex: '0 1 140px' }}>
                <Lbl>Tier group</Lbl>
                <select
                  style={inputStyle}
                  value={newTier}
                  onChange={(e) => setNewTier(e.target.value as ServiceTier)}
                  aria-label="New service tier group"
                >
                  <option value="CUSTOM">Custom / ancillary</option>
                  <option value="L1A">L1A group</option>
                  <option value="L2B">L2B group</option>
                  <option value="L1C">L1C group</option>
                </select>
              </div>
              <div style={{ flex: '2 1 260px' }}>
                <Lbl>Display name (Set-4 style)</Lbl>
                <input
                  style={inputStyle}
                  value={newFields.name}
                  onChange={(e) => patchNew({ name: e.target.value })}
                  placeholder="e.g. The Land Path · Bhumi"
                  aria-label="New service display name"
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
              <div style={{ flex: '1 1 140px' }}>
                <Lbl>Standard price (₹)</Lbl>
                <input
                  style={inputStyle}
                  type="number"
                  min={0}
                  value={newFields.rINR}
                  onChange={(e) => patchNew({ rINR: Number(e.target.value) })}
                  aria-label="New service INR price"
                />
              </div>
              <div style={{ flex: '1 1 140px' }}>
                <Lbl>Standard price ($)</Lbl>
                <input
                  style={inputStyle}
                  type="number"
                  min={0}
                  value={newFields.rUSD}
                  onChange={(e) => patchNew({ rUSD: Number(e.target.value) })}
                  aria-label="New service USD price"
                />
              </div>
              <div style={{ flex: '1 1 160px' }}>
                <Lbl>HSN</Lbl>
                <select
                  style={inputStyle}
                  value={newFields.defaultHsn}
                  onChange={(e) => patchNew({ defaultHsn: e.target.value })}
                  aria-label="New service HSN"
                >
                  {HSN_OPTIONS.map((h) => (
                    <option key={h.value} value={h.value}>
                      {h.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <Lbl>Milestones</Lbl>
            {newFields.defaultMilestones.map((m, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                <input
                  style={{ ...inputStyle, flex: '2 1 160px', width: 'auto' }}
                  value={m.label}
                  onChange={(e) => patchNewMilestone(i, { label: e.target.value })}
                  aria-label={`Milestone ${i + 1} label`}
                />
                <input
                  style={{ ...inputStyle, flex: '0 1 90px', width: 'auto' }}
                  type="number"
                  min={0}
                  max={100}
                  value={m.pct}
                  onChange={(e) => patchNewMilestone(i, { pct: Number(e.target.value) })}
                  aria-label={`Milestone ${i + 1} percent`}
                />
                <select
                  style={{ ...inputStyle, flex: '1 1 150px', width: 'auto' }}
                  value={m.dueRule}
                  onChange={(e) => patchNewMilestone(i, { dueRule: e.target.value })}
                  aria-label={`Milestone ${i + 1} due rule`}
                >
                  {DUE_RULE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              {!canCreate && !createBusy && (
                <span style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginRight: 'auto' }}>
                  Give it a code (2+ chars) and a display name to enable Create.
                </span>
              )}
              <Btn3 intent="ghost" size="sm" onClick={() => setCreating(false)} disabled={createBusy}>
                Cancel
              </Btn3>
              <Btn3 intent="grn" size="sm" onClick={doCreate} disabled={!canCreate}>
                {createBusy ? 'Creating…' : 'Create service'}
              </Btn3>
            </div>
          </Card>
        )}
      </div>

      {loadErr && rows === null && (
        <EmptyNote title="Could not load the rate card">
          Check your connection and reload. If the <code>services/</code>
          collection is empty, run the seed (<code>npm run seed:ratecard</code>)
          against the target project first.
        </EmptyNote>
      )}

      {rows === null && !loadErr && (
        <EmptyNote title="Loading the rate card…">
          Reading the services catalogue.
        </EmptyNote>
      )}

      {rows !== null && rows.length === 0 && (
        <EmptyNote title="No services found">
          The <code>services/</code> collection is empty. Run the seed
          (<code>npm run seed:ratecard</code>) to populate the refreshed
          catalogue, then reload.
        </EmptyNote>
      )}

      {rows && rows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((svc) => {
            const isOpen = expandedId === svc.svcId;
            return (
              <Card key={svc.svcId} flat>
                {/* Row header */}
                <button
                  type="button"
                  onClick={() => open(svc)}
                  style={{
                    display: 'flex',
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    alignItems: 'center',
                    gap: 10,
                    textAlign: 'left',
                    padding: 0,
                  }}
                >
                  <Tag kind="tag">{svc.tier}</Tag>
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
                    {svc.name}
                  </span>
                  <span
                    style={{
                      fontSize: 12.5,
                      color: 'var(--ink-soft)',
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {svc.isFormula
                      ? 'Formula'
                      : `${formatINR(svc.rINR)} · ${formatUSD(svc.rUSD)}`}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                    {isOpen ? '▲' : '▼'}
                  </span>
                </button>

                {isOpen && edit && (
                  <div
                    style={{
                      borderTop: '1px solid var(--rule)',
                      marginTop: 12,
                      paddingTop: 12,
                    }}
                  >
                    {/* Identity (read-only) */}
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        flexWrap: 'wrap',
                        fontSize: 12,
                        color: 'var(--ink-soft)',
                        marginBottom: 4,
                      }}
                    >
                      <span>
                        <b>ID</b> <code>{svc.svcId}</code>
                      </span>
                      <span>
                        <b>Tier</b> {svc.tier}
                      </span>
                      <span>
                        <b>Unit</b> {svc.unit}
                      </span>
                      <span>
                        <b>Firms</b>{' '}
                        {svc.firmCompat.length
                          ? svc.firmCompat.join(', ')
                          : '—'}
                      </span>
                    </div>

                    <Lbl>Name (client-facing)</Lbl>
                    <input
                      style={inputStyle}
                      value={edit.name}
                      onChange={(e) => patch({ name: e.target.value })}
                    />

                    <Lbl>Short description</Lbl>
                    <input
                      style={inputStyle}
                      value={edit.desc}
                      onChange={(e) => patch({ desc: e.target.value })}
                    />

                    {svc.isFormula ? (
                      <L3BFormulaPanel />
                    ) : (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns:
                            'repeat(auto-fit, minmax(150px, 1fr))',
                          gap: 12,
                        }}
                      >
                        <div>
                          <Lbl>Rate — INR ₹</Lbl>
                          <input
                            type="number"
                            min={0}
                            style={inputStyle}
                            value={edit.rINR}
                            onChange={(e) =>
                              patch({ rINR: Number(e.target.value) || 0 })
                            }
                          />
                        </div>
                        <div>
                          <Lbl>Rate — USD $</Lbl>
                          <input
                            type="number"
                            min={0}
                            style={inputStyle}
                            value={edit.rUSD}
                            onChange={(e) =>
                              patch({ rUSD: Number(e.target.value) || 0 })
                            }
                          />
                        </div>
                        <div>
                          <Lbl>HSN</Lbl>
                          <select
                            style={inputStyle}
                            value={edit.defaultHsn}
                            onChange={(e) =>
                              patch({ defaultHsn: e.target.value })
                            }
                          >
                            {HSN_OPTIONS.some(
                              (o) => o.value === edit.defaultHsn,
                            ) ? null : (
                              <option value={edit.defaultHsn}>
                                {edit.defaultHsn}
                              </option>
                            )}
                            {HSN_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {/* L3B: HSN still editable (it carries an HSN even though
                        the price is formula-driven). */}
                    {svc.isFormula && (
                      <div style={{ maxWidth: 280 }}>
                        <Lbl>HSN</Lbl>
                        <select
                          style={inputStyle}
                          value={edit.defaultHsn}
                          onChange={(e) =>
                            patch({ defaultHsn: e.target.value })
                          }
                        >
                          {HSN_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <Lbl>Deliverables (what the client receives)</Lbl>
                    <textarea
                      style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }}
                      value={edit.deliverables}
                      onChange={(e) => patch({ deliverables: e.target.value })}
                    />

                    {/* Milestone template */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        margin: '14px 0 6px',
                      }}
                    >
                      <Lbl>Default milestone template</Lbl>
                      <Btn3 intent="ghost" size="sm" onClick={addMilestone}>
                        + Add
                      </Btn3>
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--ink-mute)',
                        marginBottom: 8,
                      }}
                    >
                      Auto-fills when this service is added to a quote. The %
                      values are manual — they do not auto-balance here.
                    </div>

                    {edit.defaultMilestones.length === 0 && (
                      <div
                        style={{
                          fontSize: 12.5,
                          color: 'var(--ink-soft)',
                          padding: '8px 0',
                        }}
                      >
                        No milestone template.
                      </div>
                    )}

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                      }}
                    >
                      {edit.defaultMilestones.map((m, idx) => (
                        <div
                          key={idx}
                          style={{
                            border: '1px solid var(--rule)',
                            borderRadius: 'var(--r-sm)',
                            padding: 8,
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              gap: 8,
                              alignItems: 'center',
                              flexWrap: 'wrap',
                            }}
                          >
                            <input
                              style={{ ...inputStyle, flex: '2 1 160px' }}
                              placeholder="Stage label"
                              value={m.label}
                              onChange={(e) =>
                                patchMilestone(idx, { label: e.target.value })
                              }
                            />
                            <input
                              type="number"
                              min={0}
                              max={100}
                              style={{
                                ...inputStyle,
                                flex: '0 1 70px',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                              placeholder="%"
                              value={m.pct}
                              onChange={(e) =>
                                patchMilestone(idx, {
                                  pct: Math.max(
                                    0,
                                    Math.min(100, Number(e.target.value) || 0),
                                  ),
                                })
                              }
                            />
                            <select
                              style={{ ...inputStyle, flex: '1 1 130px' }}
                              value={m.dueRule}
                              onChange={(e) =>
                                patchMilestone(idx, { dueRule: e.target.value })
                              }
                            >
                              {DUE_RULE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                            <label
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                fontSize: 12,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={m.isAdvance}
                                onChange={(e) =>
                                  patchMilestone(idx, {
                                    isAdvance: e.target.checked,
                                  })
                                }
                              />
                              Advance
                            </label>
                            <Btn3
                              intent="ghost"
                              size="sm"
                              onClick={() => removeMilestone(idx)}
                              aria-label="Remove milestone"
                            >
                              ×
                            </Btn3>
                          </div>
                          <input
                            style={{ ...inputStyle, marginTop: 6 }}
                            placeholder="Deliverables at this stage (optional)…"
                            value={m.deliverables ?? ''}
                            onChange={(e) =>
                              patchMilestone(idx, {
                                deliverables: e.target.value,
                              })
                            }
                          />
                        </div>
                      ))}
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 8,
                        marginTop: 14,
                      }}
                    >
                      <Btn3
                        intent="ghost"
                        size="sm"
                        onClick={() => {
                          setExpandedId(null);
                          setEdit(null);
                        }}
                        disabled={saving}
                      >
                        Cancel
                      </Btn3>
                      <Btn3
                        intent="grn"
                        size="sm"
                        onClick={() => save(svc.svcId)}
                        disabled={saving}
                      >
                        {saving ? 'Saving…' : 'Save'}
                      </Btn3>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </Box>
  );
}

/**
 * Read-only L3B formula summary + a live worked example, so the admin sees
 * what drives L3B (the calculator) instead of a single base price. The
 * constants are LOCKED — they live in firebase-functions/.../l3bCalculator.js
 * and are mirrored 1:1 in lib/rateCard.ts; this panel reflects them.
 */
function L3BFormulaPanel() {
  const example = useMemo(
    () =>
      calculateL3B({
        floors: [
          { label: 'Floor 1', sqft: 6000 },
          { label: 'Floor 2', sqft: 6000 },
        ],
        currency: 'INR',
      }),
    [],
  );
  return (
    <div
      style={{
        border: '1px dashed var(--rule)',
        borderRadius: 'var(--r-sm)',
        padding: 12,
        marginTop: 4,
        fontSize: 12.5,
        color: 'var(--ink-soft)',
        lineHeight: 1.6,
      }}
    >
      <div style={{ fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>
        Formula-priced (scale calculator — base rate not editable)
      </div>
      ₹80,000 / $560 per 1,000 sqft per floor · band taper 100% (≤5k) / 80%
      (5–10k) / 60% (&gt;10k) on total sqft · floor multiplier 100% first, 80%
      each additional · minimum {formatINR(50000)} / {formatUSD(625)} ·
      basement 100%, stilt 100%, mezzanine 50%.
      <div
        style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid var(--rule)',
          color: 'var(--ink)',
        }}
      >
        <b>Worked example</b> — 12,000 sqft across 2 floors (6,000 each), IN ={' '}
        <b>{formatINR(example.amount)}</b> (effective rate 85% of base; Floor 1
        ₹4,08,000 + Floor 2 ₹3,26,400).
      </div>
    </div>
  );
}
